import { useCallback, useState } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import { invalidateAvatar } from '../utils/avatarManager';
import logger from '../utils/logger';
import { ALLOWED_IMAGE_TYPES, MAX_AVATAR_SIZE_BYTES } from '../constants';

/**
 * Hook for managing room profile operations
 * Handles room avatar upload and removal
 *
 * Key difference from useProfile (user avatars):
 * - Uses client.sendStateEvent() instead of client.setAvatarUrl()
 * - Sets m.room.avatar state event in the room
 * - No custom events needed (Matrix SDK emits RoomStateEvent.Events automatically)
 *
 * @param {string} roomId - Matrix room ID (!xxx:server)
 * @returns {Object} Room profile management functions and state
 */
export const useRoomProfile = (roomId) => {
  const client = useMatrixClient();
  const [uploadProgress, setUploadProgress] = useState(0);

  /**
   * Upload and set room avatar
   * @param {File} file - Image file to upload as room avatar
   * @returns {Promise<string>} MXC URL of uploaded avatar
   * @throws {Error} If client/roomId unavailable, invalid file type/size, or upload fails
   */
  const uploadRoomAvatar = useCallback(async (file) => {
    if (!client || !roomId) {
      throw new Error('Matrix client or room ID not available');
    }

    if (!file) {
      throw new Error('No file provided');
    }

    // Validate file type - only allow specific image types for security
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      throw new Error('INVALID_FILE_TYPE');
    }

    // Validate file size
    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      throw new Error('FILE_TOO_LARGE');
    }

    try {
      setUploadProgress(0);
      logger.info('Uploading room avatar:', roomId, file.name);

      // 1. Upload file to Matrix content repository
      const uploadResponse = await client.uploadContent(file, {
        name: file.name,
        type: file.type,
        progressHandler: (progress) => {
          if (progress.total > 0) {
            const percentage = Math.round((progress.loaded / progress.total) * 100);
            setUploadProgress(percentage);
          }
        },
      });

      // 2. Extract MXC URL from response
      const mxcUrl = uploadResponse.content_uri || uploadResponse;
      logger.debug('Room avatar uploaded, MXC URL:', mxcUrl);

      // 3. Set room avatar via state event (KEY DIFFERENCE FROM USER AVATARS)
      // For rooms, we use sendStateEvent instead of setAvatarUrl
      await client.sendStateEvent(
        roomId,
        'm.room.avatar',
        { url: mxcUrl },
        '',  // state_key (empty string for room avatar)
      );
      logger.info('Room avatar set successfully:', roomId, mxcUrl);

      // 4. Invalidate cache to force refresh
      await invalidateAvatar(roomId);

      // 5. Matrix SDK will automatically emit RoomStateEvent.Events
      // No need for custom event - useRoomAvatar already listens to RoomStateEvent.Events
      // and filters for EventType.RoomAvatar ('m.room.avatar')

      setUploadProgress(100);
      return mxcUrl;
    } catch (error) {
      logger.error('Failed to upload room avatar:', error);
      setUploadProgress(0);
      throw error;
    }
  }, [client, roomId]);

  /**
   * Remove room avatar
   * @returns {Promise<void>}
   * @throws {Error} If client/roomId unavailable or removal fails
   */
  const removeRoomAvatar = useCallback(async () => {
    if (!client || !roomId) {
      throw new Error('Matrix client or room ID not available');
    }

    try {
      logger.info('Removing room avatar:', roomId);

      // Remove avatar by setting empty URL
      await client.sendStateEvent(
        roomId,
        'm.room.avatar',
        { url: '' },
        '',  // state_key (empty string)
      );

      // Invalidate cache to force refresh
      await invalidateAvatar(roomId);

      logger.info('Room avatar removed successfully:', roomId);
    } catch (error) {
      logger.error('Failed to remove room avatar:', error);
      throw error;
    }
  }, [client, roomId]);

  return {
    uploadRoomAvatar,
    removeRoomAvatar,
    uploadProgress,
  };
};

export default useRoomProfile;
